import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import queryString from 'query-string';

import * as actions from 'containers/Authentication/actions';

import LoginPageStyles from './style';

import UMSLoginForm from './UMSLoginForm';
import {
  buildGoogleProfileFromIdToken,
  getJWTIssuer,
  GOOGLE_ID_TOKEN_ISSUER,
  isAuthorizedDomain,
  renderGoogleSignInButton,
} from '../helpers';
import useHeader from 'containers/App/Header/useHeader';
import { useGetUMSIdForGoogleLoginMutation } from 'services/api/ums';
import { useGetConfigQuery } from 'services/api';
import { track } from 'analytics/posthog';

const LoginPage = ({ loginUser, receiveUserError }) => {
  useHeader('Login');
  const location = useLocation();
  const [getUMSIdForGoogleLogin] = useGetUMSIdForGoogleLoginMutation();
  const {
    data: runtimeConfig,
    isLoading: isRuntimeConfigLoading,
    isError: isRuntimeConfigError,
  } = useGetConfigQuery();
  const googleButtonRef = React.useRef<HTMLDivElement | null>(null);
  const [isGoogleButtonLoading, setIsGoogleButtonLoading] =
    React.useState(true);
  const [isGoogleLoginPending, setIsGoogleLoginPending] = React.useState(false);
  const [googleLoginError, setGoogleLoginError] = React.useState('');

  React.useEffect(() => {
    track('login page viewed', {
      surface: 'login',
      referrer_present: Boolean(location?.search),
    });
  }, [location?.search]);

  const getGoogleErrorMessage = React.useCallback(error => {
    return String(
      error?.data?.message ||
        error?.data?.error ||
        error?.error ||
        error?.details ||
        error?.error_description ||
        error?.message ||
        'Google login failed',
    );
  }, []);

  const handleGoogleCredential = React.useCallback(
    async response => {
      setGoogleLoginError('');
      setIsGoogleLoginPending(true);

      const credential = response?.credential;
      if (!credential) {
        const errorMessage = getGoogleErrorMessage({
          error: 'missing_google_credential',
        });
        track('login failed', {
          login_method: 'Google',
          error_code_or_message: errorMessage,
          surface: 'login',
        });
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleLoginPending(false);
        return;
      }

      const storedAppToken = localStorage.getItem('authorization-token') || '';
      const credentialIssuer = getJWTIssuer(credential);
      const storedAppTokenIssuer = getJWTIssuer(storedAppToken);

      console.debug('[google-login] received credential', {
        tokenSource: 'GIS credential',
        issuer: credentialIssuer,
        hasStoredAppToken: Boolean(storedAppToken),
        storedAppTokenIssuer,
      });

      if (credentialIssuer !== GOOGLE_ID_TOKEN_ISSUER) {
        const errorMessage = 'Google login failed: invalid Google token issuer.';
        track('login failed', {
          login_method: 'Google',
          error_code_or_message: errorMessage,
          surface: 'login',
        });
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleLoginPending(false);
        return;
      }

      const profileObj = buildGoogleProfileFromIdToken(credential);

      track('login attempted', {
        login_method: 'Google',
        surface: 'login',
      });

      if (!isAuthorizedDomain(profileObj.email)) {
        const errorMessage = 'account domain is not authorized.';
        track('login failed', {
          login_method: 'Google',
          error_code_or_message: errorMessage,
          surface: 'login',
        });
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleLoginPending(false);
        return;
      }

      try {
        console.debug('[google-login] submitting token', {
          tokenSource: 'GIS credential',
          issuer: credentialIssuer,
        });
        const umsResponse = await getUMSIdForGoogleLogin(credential).unwrap();
        const umsId = umsResponse?.data?.id;
        const loginData: any = {
          profileObj: {
            ...profileObj,
            type: isAuthorizedDomain(profileObj.email)
              ? 'internal'
              : 'external',
          },
          tokenId: credential,
          tokenObj: {
            provider: 'google',
            refreshable: false,
            selectBy: response?.select_by || null,
          },
          tokenType: 'Google',
          umsId,
        };

        if (location?.search) {
          loginData.referrer = queryString.parse(location.search).referrer;
        }

        loginUser(loginData);
      } catch (error) {
        const errorMessage = getGoogleErrorMessage(error);
        track('login failed', {
          login_method: 'Google',
          error_code_or_message: errorMessage,
          surface: 'login',
        });
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
      } finally {
        setIsGoogleLoginPending(false);
      }
    },
    [
      getGoogleErrorMessage,
      getUMSIdForGoogleLogin,
      location?.search,
      loginUser,
      receiveUserError,
    ],
  );

  React.useEffect(() => {
    let isMounted = true;
    const buttonElement = googleButtonRef.current;

    if (!buttonElement) return undefined;

    setGoogleLoginError('');
    setIsGoogleButtonLoading(true);

    if (isRuntimeConfigLoading) return undefined;

    const googleClientId = runtimeConfig?.googleClientId;
    if (!googleClientId) {
      if (isMounted) {
        const errorMessage = isRuntimeConfigError
          ? 'Unable to load Google sign-in configuration.'
          : 'Google sign-in is not configured.';
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleButtonLoading(false);
      }
      return undefined;
    }

    renderGoogleSignInButton({
      element: buttonElement,
      clientId: googleClientId,
      onCredential: response => {
        if (isMounted) {
          handleGoogleCredential(response);
        }
      },
      onError: error => {
        if (!isMounted) return;

        const errorMessage = getGoogleErrorMessage(error);
        track('login failed', {
          login_method: 'Google',
          error_code_or_message: errorMessage,
          surface: 'login',
        });
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleButtonLoading(false);
      },
    })
      .then(() => {
        if (isMounted) {
          setIsGoogleButtonLoading(false);
        }
      })
      .catch(error => {
        if (!isMounted) return;

        const errorMessage = getGoogleErrorMessage(error);
        setGoogleLoginError(errorMessage);
        receiveUserError({ error: errorMessage });
        setIsGoogleButtonLoading(false);
      });

    return () => {
      isMounted = false;
      buttonElement.innerHTML = '';
    };
  }, [
    getGoogleErrorMessage,
    handleGoogleCredential,
    isRuntimeConfigError,
    isRuntimeConfigLoading,
    receiveUserError,
    runtimeConfig?.googleClientId,
  ]);

  const googleStatusMessage = isGoogleLoginPending
    ? 'Signing in with Google...'
    : isGoogleButtonLoading
      ? 'Loading Google sign-in...'
      : '';

  return (
    <LoginPageStyles>
      <Container fluid className="login-container">
        <UMSLoginForm onSuccess={loginUser} onError={receiveUserError} />
        <div className="seperator">
          <span className="seperator-line"></span>
          <span className="seperator-text">OR</span>
          <span className="seperator-line"></span>
        </div>
        <Row>
          <Col xs={12} sm={12} md={12} lg={12}>
            <div className="login-button-container">
              <div ref={googleButtonRef} />
            </div>
            {googleStatusMessage ? (
              <div className="google-login-status">{googleStatusMessage}</div>
            ) : null}
            {googleLoginError ? (
              <div className="error-message">{googleLoginError}</div>
            ) : null}
          </Col>
        </Row>
      </Container>
    </LoginPageStyles>
  );
};

LoginPage.propTypes = {
  loginUser: PropTypes.func,
  receiveUserError: PropTypes.func,
  loggedIn: PropTypes.bool,
};

const mapStateToProps = state => ({
  loggedIn: get(state, 'session.loggedIn'),
});

const mapDispatchToProps = dispatch => ({
  loginUser: data => dispatch(actions.loginUser(data)),
  receiveUserError: data => dispatch(actions.receiveUserError(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
